"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeLeastExpensivePromotions = computeLeastExpensivePromotions;
const cents_1 = require("../money/cents");
const validate_1 = require("./validate");
// Famille « le moins cher offert » (v1 : __getLeastExpensivePromotionsComputed,
// apps/os/src/v1/lib/promotion.ts:8-106).
//
// Règles portées telles quelles :
// - seuls les PRODUITS entrent dans le vivier (la v1 filtre ItemType.PRODUCT) ;
// - le vivier est trié par prix unitaire décroissant et dupliqué par quantité ;
// - l'article offert est celui d'indice `neededCount + offeredCount − 1`, soit
//   le moins cher du lot ; les articles du lot sont consommés (une promotion
//   suivante ne peut plus les reprendre) ;
// - la remise vaut le prix de l'article (discountValue à 0), un pourcentage de
//   ce prix, ou prix − discountValue en mode FIXED (getComputedDiscount).
//
// Écart documenté : la v1 répète une promotion en la faisant figurer plusieurs
// fois dans la liste ; ici `cumulative` autorise la répétition tant qu'il reste
// assez d'articles éligibles (une seule application sinon).
// F4 : une valeur négative ne doit jamais retomber silencieusement sur
// « article offert » — elle fait échouer le calcul.
function assertDiscountValue(promotion) {
    if (promotion.discountMode === 'PERCENTAGE') {
        (0, validate_1.assertPromotionPercent)(promotion.discountValue, 'leastExpensive.discountValue');
    }
    else {
        (0, validate_1.assertPromotionAmount)(promotion.discountValue, 'leastExpensive.discountValue');
    }
}
function discountFor(unitPrice, promotion) {
    if (promotion.discountValue > 0 && promotion.discountMode === 'PERCENTAGE') {
        return Math.min(unitPrice, (0, cents_1.applyPercent)(unitPrice, promotion.discountValue));
    }
    if (promotion.discountValue > 0) {
        // FIXED : discountValue est le prix remisé. Bornée à zéro — la v1 rendait
        // une remise négative quand le prix remisé dépassait le prix (R3).
        return Math.max(0, unitPrice - promotion.discountValue);
    }
    // discountValue à 0 : article entièrement offert (v1 getComputedDiscount).
    return unitPrice;
}
function isDiscountable(unit, promotion) {
    if (!promotion.discountableReferenceIds.includes(unit.referenceId))
        return false;
    return promotion.minPrice === undefined || unit.unitPrice >= promotion.minPrice;
}
function computeLeastExpensivePromotions(units, promotions) {
    if (promotions.length === 0)
        return [];
    let pool = units
        .filter((unit) => unit.kind === 'product')
        .sort((a, b) => b.unitPrice - a.unitPrice || a.itemIndex - b.itemIndex);
    return promotions.map((promotion) => {
        assertDiscountValue(promotion);
        const needed = promotion.neededCount > 0 ? promotion.neededCount : 1;
        const offered = promotion.offeredCount > 0 ? promotion.offeredCount : 1;
        const lotSize = needed + offered;
        let orderDiscount = 0;
        let discountedReferenceId;
        for (;;) {
            const appliable = pool.filter((unit) => isDiscountable(unit, promotion));
            if (appliable.length < lotSize)
                break;
            const target = appliable[lotSize - 1];
            if (target === undefined)
                break;
            const lot = appliable.slice(0, lotSize);
            pool = pool.filter((unit) => !lot.includes(unit));
            orderDiscount += discountFor(target.unitPrice, promotion);
            // DETTE : en cumul (plusieurs lots offerts par la même promotion), seule
            // la référence du PREMIER lot est retenue — l'adaptateur v1 n'en
            // reconstruit donc qu'une `discountedProductVariation`. Inatteignable
            // depuis la v1, qui répète la promotion en la listant plusieurs fois et
            // convertit chaque occurrence avec `cumulative: false` (from-v1-order) :
            // chaque lot a alors sa propre promotion, donc sa propre référence. À
            // reprendre le jour où un appelant passera une promotion cumulative
            // (`discountedReferenceIds`, une par lot).
            discountedReferenceId ??= target.referenceId;
            if (!promotion.cumulative)
                break;
        }
        const computed = {
            promotionId: promotion.id,
            family: 'LEAST_EXPENSIVE',
            orderDiscount,
            itemsDiscounts: [],
            totalDiscount: orderDiscount,
        };
        if (discountedReferenceId !== undefined) {
            computed.discountedReferenceId = discountedReferenceId;
        }
        return computed;
    });
}
